import React, { useState, useEffect, useRef, useMemo } from "react";
import { COPY_STRINGS } from "Constants/translatedCopy";
import { sleep } from "../shared/util";
import { Container } from "../shared";
import { getAudioForPhonemes } from "../shared/phonemeToAudio";
import useConversation from "../shared/useConversation";
import useVersion from "../shared/useVersion";
import "./UpAndDown.scss";
import icon from "./upanddown-icon.png";
import ListeningStatus from "Components/Global/ListeningStatus";
import { listen, abortListen } from "Services/Listen";
import { getUserData } from "Services/API";
import Slide from "./Slide";
import { ipaToAmirabet } from "../../../app/services/API";
import useBlendingTaskPlacementListening from "../shared/hooks/useBlendingTaskPlacementListening";
import useIsMounted from "../shared/hooks/useIsMounted";
import useCancellablePromise from "../shared/hooks/useCancellablePromise";
import UpAndDownAssessment from "./UpAndDownAssessment";

let ttsStrings;

const SPANISH_DEMO = {
  DEMO_WORD: "gato",
  DEMO_PHON: "g'a/to",
  DEMO_GRAPHEMES: ["g", "a", "t", "o"],
}

const ENGLISH_DEMO_INTERVENTION = {
  DEMO_WORD: "cat",
  DEMO_PHON: "kˈæt",
  DEMO_GRAPHEMES: ["c", "a", "t"],
}

const ENGLISH_DEMO_TUTOR = {
  DEMO_WORD: "can",
  DEMO_PHON: "kˈæn",
  DEMO_GRAPHEMES: ["c", "a", "n"],
}

// max amount of time to listen for a phoneme (or the word) before auto advancing
// TODO: share this w listen?
const MAX_LISTEN_TIME = 5000;
let maxListenTimeout = null;

export default function UpAndDownValidation(props) {
  ttsStrings = COPY_STRINGS.intervention_strings.up_and_down;

  const { id } = useVersion(props.wordMetadata, {
    1: ["PHON", "GRAPHEMIC_BREAKDOWN"],
    2: ["PHON", "GRAPHEMIC_BREAKDOWN"],
    3: ["PHON", "GRAPHEMIC_BREAKDOWN"],
    4: ["PHON", "GRAPHEMIC_BREAKDOWN"],
  });

  const versionId = props.version || id;

  const isMountedRef = useIsMounted();

  useEffect(() => {
    if (!versionId) close();
  }, [versionId]);

  if (!versionId) return null;

  const Version = {
    1: UpAndDown1,
    2: UpAndDown1,
    3: UpAndDown1,
    4: UpAndDownAssessment,
  }[versionId];

  return (
    <Version
      {...props}
      close={close}
      stopListen={stopListen}
      versionId={versionId}
      isMountedRef={isMountedRef}
      dispatchEvent={dispatchEvent}
    />
  );

  function dispatchEvent(event) {
    props.handleMessage({
      event,
    });
  }


  function close() {
    if (!isMountedRef.current) return;

    props.speech.stopListening();
    props.handleMessage({
      continueWithoutSpeaking: true,
      endOfPhrase: props.endOfPhrase,
    });
  }

  function stopListen() {
    abortListen(props.speech, props.endOfPhrase);
  }
}

function getDemoObject(tutorMode, locale){
  if(locale === 'es-mx'){
    return SPANISH_DEMO;
  } else if(tutorMode){
    return ENGLISH_DEMO_TUTOR;
  } else {
    return ENGLISH_DEMO_INTERVENTION;
  }
}

function UpAndDown1 (props) {
  const { locale } = getUserData();

  const { example, word, wordMetadata, speech, ftue, tutorMode, phrase, phonemePlacementEnabled, isInstruction, hasInstructions, currentWordIndex, story, currentPageIndex } = props;

  const { DEMO_WORD, DEMO_PHON, DEMO_GRAPHEMES } = useMemo(() => {
    if(example){
      return {
        DEMO_WORD: example.word,
        DEMO_PHON: example.PHON,
        DEMO_GRAPHEMES: example.GRAPHEMIC_BREAKDOWN.split("/"),
      }
    }
    return getDemoObject(tutorMode, locale);
  }, [example])

  const DEMO_FILES = getAudioForPhonemes(DEMO_PHON, locale);

  const { PHON, GRAPHEMIC_BREAKDOWN } = wordMetadata;

  const graphemes = GRAPHEMIC_BREAKDOWN.split("/");
  const abPhonemes = ipaToAmirabet(PHON);

  const [stage, setStage] = useState(ftue ? 0 : 1);
  const [interactive, setInteractive] = useState(false);

  const [graphemeIndex, setGraphemeIndex] = useState(-1);
  const graphemeIndexRef = useRef();
  graphemeIndexRef.current = graphemeIndex; //TODO: there MUST be a better way to do this
  const givingAllGraphemesRef = useRef(false);

  const [hasPromptedPhonemes, setHasPromptedPhonemes] = useState(false);

  const { speak, speakSequence, speakSlowly, speakFile, listenPhoneme, listenWord } =
    useConversation(speech, listen);
  const { cancellablePromise } = useCancellablePromise();

  const files = getAudioForPhonemes(PHON, getUserData().locale);

  const totalPhrases = useMemo(() => {
    return story && story.phrases ? story.phrases.length : 0;
  }, [story])

  const {
    listenForPhonemesComplete,
    listenForBlendedWordComplete,
    blendedWordListeningCompleteCallback,
  } = useBlendingTaskPlacementListening({
    currentWordIndex,
    phrase,
    speech,
    enabled: tutorMode,
    graphemeIndexRef: graphemeIndexRef,
    onPhonemeComplete: async () => {
      try {
        if (givingAllGraphemesRef.current) {
          // If we are givingAllGraphemes === true, then after brief pause give user the next grapheme
          // which will trigger onPhonemeComplete, continuing the loop until we reach onAllPhonemesComplete
          await cancellablePromise(sleep(1500));
          giveCurrentGrapheme();
        } else {
          startMaxListenTimeout(graphemeIndexRef.current, beginGiveAllGraphemes);
        }
      } catch (error) {
        if (error.isCancelled) return;
        throw(error);
      }
    },
    onAllPhonemesComplete: clearMaxListenTimeout,
    onBlendedWordComplete: clearMaxListenTimeout,
    onWordIndexAdvanced: (newWordIndex) => {
      setGraphemeIndex(newWordIndex - 1)
    },
    onPhonemeListenStart,
    onBlendedWordListenStart
  });

  // handle stages
  useEffect(() => {
    onStageChange();
  }, [stage, isInstruction]);

  // clear timer on unmount
  useEffect(() => {
    return () => {
      clearMaxListenTimeout();
    };
  }, []);

  function clearMaxListenTimeout(){
    if(maxListenTimeout){
      window.clearTimeout(maxListenTimeout);
    }
  }

  function startMaxListenTimeout(currentGraphemeIndex, callbackOverride = null){
    clearMaxListenTimeout();
    const defaultCallback = () => {
      if (!props.isMountedRef.current) return;
      props.skipWord(false, true);
    };
    const timeoutCallback = callbackOverride || defaultCallback;
    maxListenTimeout = window.setTimeout(timeoutCallback, MAX_LISTEN_TIME);
  }

  async function giveCurrentGrapheme(preamble) {
    if (!props.isMountedRef.current) return;
    try {
      if (preamble) {
        await cancellablePromise(props.speech.speakWithPromise({ textToSpeak: preamble }));
      }

      await cancellablePromise(speakFile(files[graphemeIndexRef.current + 1]));
      props.skipWord(false, true);
    } catch (error) {
      if (error.isCancelled) return;
      throw(error);
    }
  }

  function beginGiveAllGraphemes() {
    if (!props.isMountedRef.current) return;
    // If user input times out during grapheme section, stop listening and initiate "givingAllGraphemes"
    // where Amira will read all remaining graphemes in order to user and then start listening again when it is time for the blended word
    props.speech.stopListening();
    givingAllGraphemesRef.current = true;
    const giveGraphemePreamble = `${ttsStrings.repeat_after_me}. <break time=\"0.5s\"/>`;
    giveCurrentGrapheme(giveGraphemePreamble)
  }

  function onPhonemeListenStart() {
    const promptNeeded = !hasPromptedPhonemes && graphemeIndex == -1;
    startMaxListenTimeout(graphemeIndex, promptNeeded ? () => {
      if (!props.isMountedRef.current) return;
      speakPromptSounds(word);
      setHasPromptedPhonemes(true);
      startMaxListenTimeout(graphemeIndex, beginGiveAllGraphemes);
    } : null);
  }

  function onBlendedWordListenStart(){
    startMaxListenTimeout(graphemeIndex, () => {
      props.speech.speak({
        textToSpeak: ttsStrings.ask_blend_tutor, // for now, we only hit this function if we're in tutorMode
        onEnd: () => {
          if (!props.isMountedRef.current) return;
          startMaxListenTimeout(graphemeIndex, () => {
            if(blendedWordListeningCompleteCallback){
              blendedWordListeningCompleteCallback(false);
            }
          });
        }
      });
    });
  }

  async function onStageChange() {
    if (!props.isMountedRef.current) return;

    try {
      if(isInstruction){
        return; // if the phrase component is currently giving instructions, our only job is to sit here and look pretty
      }
      switch (stage) {
        case 0: {
          props.speech.stopListening();
          if(tutorMode){
            await cancellablePromise(speak(ttsStrings.intro_1_tutor));
          } else {
            await cancellablePromise(speakIntro(ttsStrings.intro_1_intervention, DEMO_WORD));
          }
          await cancellablePromise(sleep());
          const length = DEMO_FILES.length;
          for (let i = 0; i < length; i++) {
            await cancellablePromise(speakFile(DEMO_FILES[i]));
            setGraphemeIndex(i);
            await cancellablePromise(sleep());
          }
          await cancellablePromise(speakSlowly({textToSpeak: DEMO_WORD, skipTranslation: getUserData().bilingualMode}));
          setGraphemeIndex(length + 1);
          await cancellablePromise(sleep());
          setGraphemeIndex(-1);
          setStage(1);
          break;
        }
        case 1: {
          if (!ftue) {
            if(!hasInstructions){
              await cancellablePromise(speakIntro(ttsStrings.intro_2, word));
            }
          } else {
            await cancellablePromise(speakAskSounds(word, tutorMode));
          }

          // listen for the phonemes to move the bear up the stairs
          if(tutorMode && phonemePlacementEnabled){
            // if we're in tutor mode and have phoneme placement enabled, use the word index coming through from the placement manager
            await cancellablePromise(listenForPhonemesComplete()); // The reason to do it this way instead of in a more "reacty" way is to keep the code structure the same for other uses (not tutor) of this component
          } else {
            // otherwise use listen.js to listen for a match
            for (let i = 0; i < graphemes.length; i++) {
              const { status } = await cancellablePromise(listenPhoneme({ file: files[i], targetWord: abPhonemes[i] })); //In the intervention, we advance the creature up the slide even on "struggle" -- for now we are not keeping this behavior for the tutor mode
              if (status === "skip") return props.close(); // for now we're not listening for "skip" keyword in tutor mode
              setGraphemeIndex(i);
            }
          }


          // listen for the blended word to move the bear down the slide
          if(tutorMode){
            // if we're in tutor mode, whether phoneme placement is enabled or not, use the word index coming through from the placement manager
            const success = await cancellablePromise(listenForBlendedWordComplete()); //The reason to do it this way instead of in a more "reacty" way is to keep the code structure the same for other uses (not tutor) of this component
            if (success) {
              await cancellablePromise(onMatch());
            } else {
              await cancellablePromise(onStruggle());
            }
            props.close();
          } else {
            // otherwise use listen.js to listen for a match

            setInteractive(true); // this makes the creature clickable when it's at the top of the slide
                                  //  clicking it makes it slide down the slide and ends the intervention
                                  //  for now, we're only doing this if we're not in tutor mode

            const { status } = await cancellablePromise(listenWord({
              targetWord: word,
              patterOverride: ["match", "struggle"],
              silencePatter: ttsStrings.ask_blend_intervention,
            }));
            if (status === "skip") return props.close(); // for now we're not listening for "skip" keyword in tutor mode
            if (status === "match") await cancellablePromise(onMatch());
            else await cancellablePromise(onStruggle());
            props.close();
          }


          break;
        }
      }
    } catch (error) {
      if (error.isCancelled) return;
      throw(error);
    }
  }

  return (
    <Container icon={icon}>
      {renderContent()}
      <div style={{ marginTop: 30 }}>
        {!tutorMode && <ListeningStatus />}
      </div>
    </Container>
  );

  function renderContent() {
    if (stage === 0) {
      return (
        <Slide
          steps={DEMO_GRAPHEMES.length}
          graphemeIndex={graphemeIndex}
          versionId={props.versionId}
          graphemes={DEMO_GRAPHEMES}
        />
      );
    }

    return (
      <Slide
        steps={graphemes.length}
        graphemeIndex={graphemeIndex}
        versionId={props.versionId}
        graphemes={graphemes}
        interactive={interactive}
        onClickCreature={async () => {
          props.stopListen();
          await onMatch();
          props.close();
        }}
      />
    );
  }

  async function speakIntro(introString, word) {
    if(getUserData().bilingualMode) {
      await speakSequence([
        introString,
        {textToSpeak: word, skipTranslation: true},
      ]);
    }
    else await speak(`${introString} ${word}`);
  }

  async function speakAskSounds(word, tutorMode) {
    const askSoundsString = tutorMode ? ttsStrings.ask_sounds_tutor : ttsStrings.ask_sounds_intervention;
    if(getUserData().bilingualMode) {
      await speakSequence([
        askSoundsString,
        {textToSpeak: word, skipTranslation: true},
      ]);
    }
    else await speak(`${askSoundsString} ${word}`);
  }

  async function speakPromptSounds(word) {
    if(getUserData().bilingualMode) {
      await speakSequence([
        ttsStrings.prompt_sounds,
        {textToSpeak: word, skipTranslation: true},
      ]);
    }
    else await speak(`${ttsStrings.prompt_sounds} ${word}`);
  }

  async function onMatch() {
    try {
      setGraphemeIndex(graphemes.length + 1);

      if(getUserData().bilingualMode) {
        await cancellablePromise(speakSequence([
          {textToSpeak: word, skipTranslation: true},
          ttsStrings.correct,
        ]));
      }
      else await cancellablePromise(speak(`${word}. ${ttsStrings.correct}`));
    } catch (error) {
      if (error.isCancelled) return;
      throw(error);
    }
  }

  async function onStruggle() {
    try {
      // Don't say 'Lets read some more' phrase if this is last phrase
      if (currentPageIndex == totalPhrases - 1) {
        if(getUserData().bilingualMode) {
          await cancellablePromise(speakSequence([
            ttsStrings.struggle_intro,
            {textToSpeak: word, skipTranslation: true},
          ]));
        }
        else await cancellablePromise(speak(`${ttsStrings.struggle_intro} ${word}.`));
      } else {
        if(getUserData().bilingualMode) {
          await cancellablePromise(speakSequence([
            ttsStrings.struggle_intro,
            {textToSpeak: word, skipTranslation: true},
            ttsStrings.struggle_end,
          ]));
        }
        else await cancellablePromise(speak(`${ttsStrings.struggle_intro} ${word}. ${ttsStrings.struggle_end}`));
      }

      setGraphemeIndex(graphemes.length + 1);
      if(props.advanceOneWord){
        props.advanceOneWord();
      }
    } catch (error) {
      if (error.isCancelled) return;
      throw(error);
    }
  }
}
